import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n/useTranslation';
import BookingModal from './BookingModal';
import Calendar from './Calendar';
import DatePicker from './DatePicker';
import SuccessModal from './SuccessModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const UserDashboard = () => {
  const { user } = useAuth();
  const { t, language } = useTranslation();
  const [slots, setSlots] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('slots');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetchSlots();
    if (user) {
      fetchMyBookings();
    }
  }, [selectedDate, user]);

  const fetchSlots = async (forceRefresh = false) => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      // Normalize date to ensure proper query
      const dateStr = selectedDate.includes('T') ? selectedDate.split('T')[0] : selectedDate;
      // Add timestamp to prevent caching when force refresh
      const params = forceRefresh ? { date: dateStr, _t: Date.now() } : { date: dateStr };
      const response = await axios.get(`${API_URL}/bookings/slots`, { params });
      console.log('Fetched slots for date:', dateStr, 'Count:', response.data.length);
      if (response.data.length > 0) {
        console.log('Sample slots:', response.data.slice(0, 3).map(s => `${s.startTime}-${s.endTime} (available: ${s.isAvailable})`));
      }
      // Direct state update
      setSlots(response.data || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyBookings = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_URL}/bookings/my-bookings`);
      setMyBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const handleBookSlot = (slot) => {
    setSelectedSlot(slot);
  };

  const handleBookingSuccess = async () => {
    setSelectedSlot(null);
    setShowSuccess(true);
    
    // Force refresh slots with cache busting
    await fetchSlots(true);
    
    // Trigger refresh in Calendar and DatePicker components
    setRefreshKey(prev => prev + 1);
    
    if (user) {
      fetchMyBookings();
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (window.confirm(t('booking.cancelConfirm'))) {
      try {
        await axios.put(`${API_URL}/bookings/${bookingId}/cancel`);
        fetchMyBookings();
        fetchSlots();
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to cancel booking');
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // Always use Gregorian calendar, but with Arabic month names if Arabic is selected
    const monthNames = language === 'ar'
      ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
      : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = language === 'ar'
      ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
      : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    const day = dayNames[date.getDay()];
    const month = monthNames[date.getMonth()];
    const dayNum = date.getDate();
    const year = date.getFullYear();
    
    return language === 'ar' 
      ? `${day}، ${dayNum} ${month} ${year}`
      : `${day}, ${month} ${dayNum}, ${year}`;
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isRTL = language === 'ar';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Hero Section - matches the prices page treatment */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-700 shadow-lg">
        <div className="absolute inset-0 opacity-10" aria-hidden="true">
          <div className="absolute -top-24 -end-24 w-72 h-72 rounded-full bg-white" />
          <div className="absolute -bottom-32 -start-16 w-96 h-96 rounded-full bg-white" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 sm:p-4 bg-white/20 backdrop-blur rounded-full">
              <svg className="w-8 h-8 sm:w-12 sm:h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3 px-2">
            {t('dashboard.title')}
          </h1>
          <div className="w-20 h-1 bg-white/40 rounded-full mx-auto mb-4" />
          <p className="text-base sm:text-xl text-blue-50 font-medium px-2">
            {t('dashboard.subtitle')}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-12 pb-8">
        {/* Tabs */}
        <div className="mb-4 sm:mb-6 bg-white rounded-xl shadow-md p-1 inline-flex w-full sm:w-auto overflow-x-auto" dir={isRTL ? 'rtl' : 'ltr'}>
          <button
            onClick={() => setActiveTab('slots')}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all text-xs sm:text-sm whitespace-nowrap ${isRTL ? 'ml-1' : 'mr-1'} ${
              activeTab === 'slots'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('dashboard.slots')}
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all text-xs sm:text-sm whitespace-nowrap ${isRTL ? 'ml-1' : 'mr-1'} ${
              activeTab === 'calendar'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('dashboard.calendar')}
          </button>
          {user && (
            <button
              onClick={() => setActiveTab('my-bookings')}
              className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'my-bookings'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('dashboard.myBookings')} ({myBookings.length})
            </button>
          )}
        </div>

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className={`grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 ${isRTL ? 'rtl' : 'ltr'}`}>
            <div className="lg:col-span-2 order-2 lg:order-1 w-full overflow-hidden">
              <Calendar
                key={refreshKey}
                onDateSelect={setSelectedDate}
                selectedDate={selectedDate}
              />
            </div>
            <div className="space-y-3 sm:space-y-4 order-1 lg:order-2 mt-2 sm:mt-0">
              <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border-l-4 border-blue-600">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                  {formatDate(selectedDate)}
                </h3>
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-6 sm:py-8 gap-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    <p className="text-sm text-gray-600 font-semibold">{t('dashboard.loadingSlots')}</p>
                  </div>
                ) : slots.length === 0 ? (
                  <div className="text-center py-6 sm:py-8">
                    <svg className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mb-2 sm:mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm sm:text-base text-gray-500">{t('dashboard.noSlots')}</p>
                  </div>
                ) : (
                  <div className="space-y-2 sm:space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                    {slots
                      .sort((a, b) => {
                        // Sort by start time
                        const timeA = a.startTime.split(':').map(Number);
                        const timeB = b.startTime.split(':').map(Number);
                        return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1]);
                      })
                      .map((slot) => (
                      <button
                        key={slot._id}
                        onClick={() => slot.isAvailable && handleBookSlot(slot)}
                        disabled={!slot.isAvailable}
                        className={`w-full py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg transition-all shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-between text-sm sm:text-base ${
                          slot.isAvailable
                            ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 cursor-pointer'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-60'
                        }`}
                      >
                        <span className="font-semibold">
                          {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                        </span>
                        {slot.isAvailable ? (
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                          </svg>
                        ) : (
                          <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">
                            {t('calendar.booked')}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Available Slots Tab */}
        {activeTab === 'slots' && (
          <div dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Date Selector */}
                <div className="mb-4 sm:mb-6 bg-white rounded-xl shadow-md p-4 sm:p-6 border-l-4 border-blue-600">
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('dashboard.selectDate')}
                  </label>
                  <DatePicker
                    key={refreshKey}
                    value={selectedDate}
                    onChange={setSelectedDate}
                    min={new Date().toISOString().split('T')[0]}
                    placeholder={t('dashboard.selectDate')}
                    className="w-full"
                  />
                </div>

            {/* Available Slots */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16 bg-white rounded-xl shadow-md gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
                <p className="text-base text-gray-600 font-semibold">{t('dashboard.loadingSlots')}</p>
              </div>
            ) : slots.length === 0 ? (
              <div className="text-center py-12 sm:py-16 bg-white rounded-xl shadow-md">
                <svg className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm sm:text-base lg:text-lg text-gray-500 px-4">{t('dashboard.noSlots')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {slots.map((slot) => (
                  <div
                    key={slot._id}
                    className={`bg-white rounded-xl shadow-md p-4 sm:p-6 transition-all transform ${slot.isAvailable ? 'hover:shadow-xl hover:scale-105' : 'opacity-60'} border-l-4 ${slot.isAvailable ? 'border-blue-500' : 'border-gray-400'}`}
                  >
                    <div className="mb-3 sm:mb-4">
                      <p className="text-xs sm:text-sm text-gray-500 mb-1">{formatDate(slot.date)}</p>
                      <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                        {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                      </p>
                    </div>
                    {slot.isAvailable ? (
                      <button
                        onClick={() => handleBookSlot(slot)}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-semibold shadow-md hover:shadow-lg text-sm sm:text-base"
                      >
                        {t('dashboard.bookNow')}
                      </button>
                    ) : (
                      <button
                        disabled
                        className="w-full bg-gray-400 text-white py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg cursor-not-allowed font-semibold shadow-md text-sm sm:text-base"
                      >
                        {t('calendar.booked')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* My Bookings Tab */}
        {activeTab === 'my-bookings' && user && (
          <div dir={isRTL ? 'rtl' : 'ltr'}>
            {myBookings.length === 0 ? (
              <div className="text-center py-12 sm:py-16 bg-white rounded-xl shadow-md">
                <svg className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="text-sm sm:text-base lg:text-lg text-gray-500 px-4">{t('dashboard.noBookings')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {myBookings.map((booking) => (
                  <div
                    key={booking._id}
                    className="bg-white rounded-xl shadow-md p-4 sm:p-6 hover:shadow-xl transition-all border-l-4 border-blue-500"
                  >
                    <div className="flex justify-between items-start mb-3 sm:mb-4 flex-wrap gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm text-gray-500 mb-1">
                          {formatDate(booking.slot.date)}
                        </p>
                        <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 break-words">
                          {formatTime(booking.slot.startTime)} - {formatTime(booking.slot.endTime)}
                        </p>
                      </div>
                      <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap ${
                        booking.status === 'confirmed'
                          ? 'bg-green-100 text-green-800'
                          : booking.status === 'cancelled'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {t(`booking.status.${booking.status}`)}
                      </span>
                    </div>
                    {Object.keys(booking.bookingFields || {}).length > 0 && (
                      <div className="mb-3 sm:mb-4 pt-3 sm:pt-4 border-t border-gray-200">
                        <p className="text-xs sm:text-sm font-medium text-gray-700 mb-2">{t('booking.details')}:</p>
                        <ul className="space-y-1">
                          {Object.entries(booking.bookingFields).map(([key, value]) => (
                            <li key={key} className="text-xs sm:text-sm text-gray-600 break-words">
                              <span className="font-medium">{key}:</span> {value}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {booking.status === 'confirmed' && (
                      <button
                        onClick={() => handleCancelBooking(booking._id)}
                        className="w-full mt-3 sm:mt-4 px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium text-sm sm:text-base"
                      >
                        {t('booking.cancel')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {selectedSlot && (
          <BookingModal
            slot={selectedSlot}
            onClose={() => setSelectedSlot(null)}
            onSuccess={handleBookingSuccess}
          />
        )}

        {showSuccess && (
          <SuccessModal onClose={() => setShowSuccess(false)} />
        )}
      </div>
    </div>
  );
};

export default UserDashboard;
